import { ISettingsChangeData } from "../../models/event/ISettingsChangeData";
import { CursorState } from "../../states/CursorState";
import { EventManager } from "../managers/EventManager";
import { StringContext } from "../StringContext";
import { StringData } from "../StringData";
import { StringToolsContainer } from "../StringToolsContainer";

export class CursorController {
  protected context: StringContext;

  private readonly SETTLE_THRESHOLD_SQ = 0.01;

  private smoothingFactor: number;

  private lastMouseX: number = 0;
  private lastMouseY: number = 0;
  private lastMouseTime: number = 0;

  private _lerpStepXArgs = { from: 0, to: 0, progress: 0 };
  private _lerpStepYArgs = { from: 0, to: 0, progress: 0 };

  constructor(smoothing: number = 0.1, context: StringContext) {
    this.smoothingFactor = smoothing;
    this.context = context;

    this.onSettingsChange({
      isDesktop: context.data.viewport.windowWidth > 1024,
      isForceRebuild: false,
      widthChanged: true,
      heightChanged: true,
      scrollHeightChanged: true,
    });
  }

  public onMouseMove(e: MouseEvent): void {
    const cursorData = this.context.data.cursor;

    cursorData.targetX = e.clientX;
    cursorData.targetY = e.clientY;

    const now = performance.now();

    const dt = Math.max(1, now - this.lastMouseTime);

    cursorData.velocityX = (e.clientX - this.lastMouseX) / dt;
    cursorData.velocityY = (e.clientY - this.lastMouseY) / dt;

    this.lastMouseX = e.clientX;
    this.lastMouseY = e.clientY;
    this.lastMouseTime = now;
  }

  public onFrame(): void {
    const cursorData = this.context.data.cursor;

    const tX = cursorData.targetX;
    const tY = cursorData.targetY;
    const sX = cursorData.smoothedX;
    const sY = cursorData.smoothedY;

    this._lerpStepXArgs.from = sX;
    this._lerpStepXArgs.to = tX;
    this._lerpStepXArgs.progress = this.smoothingFactor;

    this._lerpStepYArgs.from = sY;
    this._lerpStepYArgs.to = tY;
    this._lerpStepYArgs.progress = this.smoothingFactor;

    const stepX = this.context.tools.lerp.process(this._lerpStepXArgs);
    const stepY = this.context.tools.lerp.process(this._lerpStepYArgs);
    const distSq = stepX * stepX + stepY * stepY;
    if (distSq < this.SETTLE_THRESHOLD_SQ) {
      cursorData.smoothedX = tX;
      cursorData.smoothedY = tY;
      cursorData.stepX = 0;
      cursorData.stepY = 0;
    } else {
      cursorData.smoothedX += stepX;
      cursorData.smoothedY += stepY;
      cursorData.stepX = stepX;
      cursorData.stepY = stepY;
    }
  }

  public onSettingsChange(data: ISettingsChangeData): void {
    let lerp = Number(this.context.settings["cursor-lerp"]);
    this.setLerpFactor(lerp);
  }

  public setLerpFactor(t: number): void {
    this.smoothingFactor = this.context.tools.adaptiveLerp.process({
      value: t,
      inMin: 0.1,
      inMax: 1.0,
      outMin: 0.05,
      outMax: 0.65,
    });
  }
}
